import { Stack } from '@mui/material';
import { getYear } from 'date-fns';
import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Accordion } from '@/components/Accordion/Accordion';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { MultiDocumentUploaderWithListV2 } from '@/modules/documents/MultiDocumentUploader/MultiDocumentUploaderWithListV2';
import {
  BeneficiaryDropdownSingle,
  getOptionsFromData as getBeneficiaryOptionsFromData,
} from '@/modules/entities/inputs/BeneficiaryDropdown/BeneficiaryDropdown';
import { PrimaryClientDropdownSingle } from '@/modules/entities/inputs/PrimaryClientDropdown/PrimaryClientDropdown';
import { getPrimaryClientOptionsFromData } from '@/modules/entities/inputs/PrimaryClientDropdown/PrimaryClientDropdown.utils';
import { useIRSConstants } from '@/modules/irs/useIRSConstants';
import { StateCode } from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { getNodes } from '@/utils/graphqlUtils';

import { NON_GIFT_RECIPIENT_ENTITY_TYPES } from '../gifting.constants';
import { ClientDetailsGiftingPage_HouseholdFragment } from '../graphql/ClientDetailsGiftingPage.generated';
import { useGiftingExemptionsForClient } from '../hooks/useGiftingExemptionsForClient';
import { useLogNewGiftModalFormOptionsQuery } from './graphql/LogNewGiftModal.generated';
import { assetTypeOptions, NAMESPACE } from './LogNewGiftModal.constants';
import { LogNewGiftForm, LogNewGiftFormPaths } from './LogNewGiftModal.types';

export function useFormOptions({ householdId }: { householdId: string }) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { control } = useFormContext<LogNewGiftForm>();

  const { data } = useLogNewGiftModalFormOptionsQuery({
    variables: {
      where: {
        id: householdId,
      },
    },
    onError: (error) => {
      showFeedback(
        "We weren't able to load this form. Please refresh the page and try again."
      );
      reportError(
        'failed to load form options for the LogNewGiftModal',
        error,
        {
          householdId,
        }
      );
    },
  });

  const possibleBeneficiaries = getNodes(data?.households)[0];

  const selectedGrantor = useWatch({
    name: `${NAMESPACE}.grantor` as const satisfies LogNewGiftFormPaths,
    control,
  });

  const household = data?.households.edges?.[0]?.node ?? null;
  const primaryClientOptions = getPrimaryClientOptionsFromData(
    household,
    compact([selectedGrantor.clientProfileId])
  );

  const selectedBeneficiaries = useWatch({
    name: `${NAMESPACE}.beneficiary` as const satisfies LogNewGiftFormPaths,
    control,
  });

  const beneficiaryOptions = getBeneficiaryOptionsFromData(
    data?.households.edges?.[0]?.node ?? null,
    [selectedBeneficiaries.beneficiaryId],
    {
      // spreading the array b/c NON_GIFT_RECIPIENT_ENTITY_TYPES is readonly and filterEntityTypes is mutable
      filterEntityTypes: [...NON_GIFT_RECIPIENT_ENTITY_TYPES],
    }
  );

  return {
    grantorOptions: primaryClientOptions,
    beneficiaryOptions,
    possibleBeneficiaries,
  };
}

interface Props {
  grantorOptions: ReturnType<typeof useFormOptions>['grantorOptions'];
  beneficiaryOptions: ReturnType<typeof useFormOptions>['beneficiaryOptions'];
  householdId: string;
  advisorClient: ClientDetailsGiftingPage_HouseholdFragment;
  onAddNewRecipient: () => void;
}

export function LogNewGiftModalForm({
  grantorOptions,
  beneficiaryOptions,
  householdId,
  advisorClient,
  onAddNewRecipient,
}: Props) {
  const { control, setValue } = useFormContext<LogNewGiftForm>();

  const selectedGrantor = useWatch({
    name: `${NAMESPACE}.grantor` as const satisfies LogNewGiftFormPaths,
    control,
  });

  const isForcedBeneficiary = useWatch({
    control,
    name: `${NAMESPACE}.isForcedBeneficiary` as const satisfies LogNewGiftFormPaths,
  });

  const lifetimeExclusionEventId = useWatch({
    name: `${NAMESPACE}.lifetimeExclusionEventId` as const satisfies LogNewGiftFormPaths,
    control,
  });

  const {
    annualGiftTaxExclusionAmount,
    lifetimeExemptionAmount,
    gstExemptionAmount,
  } = useIRSConstants();

  const { grantorIdToExemptionsMap } = useGiftingExemptionsForClient({
    household: advisorClient,
  });

  const hasAnyCtGrantor = useMemo(() => {
    return advisorClient.possiblePrimaryClients.some(
      (client) => client.address?.stateCode === StateCode.Ct
    );
  }, [advisorClient.possiblePrimaryClients]);

  // Update the checkbox default when we detect CT grantors
  useEffect(() => {
    if (hasAnyCtGrantor && !lifetimeExclusionEventId) {
      // Only set to true for new gifts in CT households
      // Don't override for existing gifts (they load their own value)
      setValue(
        `${NAMESPACE}.isSubjectToCtGiftTax` as const satisfies LogNewGiftFormPaths,
        true,
        {
          shouldDirty: false,
        }
      );
    }
  }, [hasAnyCtGrantor, lifetimeExclusionEventId, setValue]);

  const isSubjectToCtGiftTax = useWatch({
    control,
    name: `${NAMESPACE}.isSubjectToCtGiftTax` as const satisfies LogNewGiftFormPaths,
  });

  const availableLifetimeExemptionForSelectedGrantor: Decimal | null =
    grantorIdToExemptionsMap[selectedGrantor.clientProfileId]
      ?.lifetimeExemptionAmount ?? null;
  const availableGSTExemptionForSelectedGrantor: Decimal | null =
    grantorIdToExemptionsMap[selectedGrantor.clientProfileId]
      ?.gstExemptionAmount ?? null;

  const annualExclusionHelpText = useMemo(() => {
    return annualGiftTaxExclusionAmount
      ? `${getYear(new Date())} limit of ${formatCurrencyNoDecimals(
          annualGiftTaxExclusionAmount
        )}/recipient`
      : '';
  }, [annualGiftTaxExclusionAmount]);

  const lifetimeExemptionHelpText = useMemo(() => {
    if (availableLifetimeExemptionForSelectedGrantor) {
      return `${formatCurrencyNoDecimals(
        availableLifetimeExemptionForSelectedGrantor
      )} available`;
    }

    return lifetimeExemptionAmount
      ? `${formatCurrencyNoDecimals(lifetimeExemptionAmount)} available`
      : '';
  }, [availableLifetimeExemptionForSelectedGrantor, lifetimeExemptionAmount]);

  const gstExemptionHelpText = useMemo(() => {
    if (availableGSTExemptionForSelectedGrantor) {
      return `${formatCurrencyNoDecimals(
        availableGSTExemptionForSelectedGrantor
      )} available`;
    }

    return gstExemptionAmount
      ? `${formatCurrencyNoDecimals(gstExemptionAmount)} available`
      : '';
  }, [availableGSTExemptionForSelectedGrantor, gstExemptionAmount]);

  return (
    <Stack flex={1} sx={{ minHeight: 450 }}>
      <FormLayoutRow>
        <FormLayoutItem
          width={6}
          sx={{
            overflow: 'hidden',
          }}
        >
          <PrimaryClientDropdownSingle<LogNewGiftForm>
            control={control}
            namespace={NAMESPACE}
            options={grantorOptions}
            labelOverride="Client"
            required
            hideGrantorContextualHelp
          />
        </FormLayoutItem>
        <FormLayoutItem
          width={6}
          sx={{
            overflow: 'hidden',
          }}
        >
          <BeneficiaryDropdownSingle<LogNewGiftForm>
            control={control}
            namespace={NAMESPACE}
            options={beneficiaryOptions}
            required
            label="Recipient"
            placeholder=""
            disabled={isForcedBeneficiary}
            addNewOption={
              isForcedBeneficiary ? undefined : { onClick: onAddNewRecipient }
            }
            // Focus returns to this input after the add-new modal closes.
            // Keep the menu closed so the new recipient is visible.
            openOnFocus={false}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareCurrencyInput<LogNewGiftForm>
            isDecimalJSInput
            control={control}
            label="Gift amount"
            required
            fieldName={
              `${NAMESPACE}.giftAmount` as const satisfies LogNewGiftFormPaths
            }
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareDatePickerInput<LogNewGiftForm>
            control={control}
            label="Date of gift"
            required
            fieldName={
              `${NAMESPACE}.dateOfGift` as const satisfies LogNewGiftFormPaths
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareCurrencyInput<LogNewGiftForm>
            isDecimalJSInput
            control={control}
            label="Discounted value of gift (optional)"
            fieldName={
              `${NAMESPACE}.discountedValueOfGift` as const satisfies LogNewGiftFormPaths
            }
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareCurrencyInput<LogNewGiftForm>
            isDecimalJSInput
            control={control}
            label="Annual exclusion used"
            required
            helpText={annualExclusionHelpText}
            fieldName={
              `${NAMESPACE}.annualExclusionApplied` as const satisfies LogNewGiftFormPaths
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareCurrencyInput<LogNewGiftForm>
            isDecimalJSInput
            control={control}
            label="Lifetime exemption used"
            required
            helpText={lifetimeExemptionHelpText}
            fieldName={
              `${NAMESPACE}.lifetimeExemptionUsed` as const satisfies LogNewGiftFormPaths
            }
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareCurrencyInput<LogNewGiftForm>
            isDecimalJSInput
            control={control}
            label="GST exemption used"
            required
            helpText={gstExemptionHelpText}
            fieldName={
              `${NAMESPACE}.gstExemptionApplied` as const satisfies LogNewGiftFormPaths
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
      {hasAnyCtGrantor && (
        <>
          <FormLayoutRow>
            <FormLayoutItem width={6}>
              <FormAwareCheckbox<LogNewGiftForm>
                control={control}
                label="Gift is subject to CT gift tax"
                fieldName={
                  `${NAMESPACE}.isSubjectToCtGiftTax` as const satisfies LogNewGiftFormPaths
                }
              />
            </FormLayoutItem>
          </FormLayoutRow>
          {isSubjectToCtGiftTax && (
            <FormLayoutRow>
              <FormLayoutItem width={6}>
                <FormAwareCurrencyInput<LogNewGiftForm>
                  isDecimalJSInput
                  control={control}
                  label="CT gift tax paid"
                  fieldName={
                    `${NAMESPACE}.ctTaxesPaid` as const satisfies LogNewGiftFormPaths
                  }
                />
              </FormLayoutItem>
            </FormLayoutRow>
          )}
        </>
      )}
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput
            fieldName={
              `${NAMESPACE}.notes` as const satisfies LogNewGiftFormPaths
            }
            label="Notes"
            control={control}
            multiline
            rows={2}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareRadioGroup<LogNewGiftForm>
            control={control}
            fieldName={
              `${NAMESPACE}.assetType` as const satisfies LogNewGiftFormPaths
            }
            row
            options={assetTypeOptions}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <Accordion variant="filled" title="Document upload">
        <FormLayoutRow>
          <FormLayoutItem>
            <MultiDocumentUploaderWithListV2
              householdId={householdId}
              variant="compact"
              allowSelectExistingDocuments={true}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      </Accordion>
    </Stack>
  );
}
